import React, { useEffect, useState } from "react";
import {
  Box, Button, Typography, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Table, TableHead,
  TableBody, TableRow, TableCell, Snackbar, Alert, FormControl,
  InputLabel, Select, MenuItem, useTheme
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";

// Icon imports
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import InfoIcon from "@mui/icons-material/Info";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import LoopIcon from "@mui/icons-material/Loop";

import { grey, blueGrey, lightGreen } from "@mui/material/colors"; // Color fallback
import api from "../../utils/api";

// Extended color fallback mapping
const customColors = {
  grey: grey[500],
  blueGrey: blueGrey[500],
  lightGreen: lightGreen[500],
};

const colorOptions = [
  { label: "Primary", value: "primary" },
  { label: "Secondary", value: "secondary" },
  { label: "Success", value: "success" },
  { label: "Error", value: "error" },
  { label: "Warning", value: "warning" },
  { label: "Info", value: "info" },
  { label: "Inherit", value: "inherit" },
  { label: "Dark (Custom)", value: "grey" },
  { label: "BlueGrey", value: "blueGrey" },
  { label: "Light Green", value: "lightGreen" },
];

const iconOptions = [
  { label: "PlayArrow", value: "PlayArrow", icon: <PlayArrowIcon fontSize="small" /> },
  { label: "PauseCircle", value: "PauseCircle", icon: <PauseCircleIcon fontSize="small" /> },
  { label: "CheckCircle", value: "CheckCircle", icon: <CheckCircleIcon fontSize="small" /> },
  { label: "Autorenew", value: "Autorenew", icon: <AutorenewIcon fontSize="small" /> },
  { label: "Info", value: "Info", icon: <InfoIcon fontSize="small" /> },
  { label: "WarningAmber", value: "WarningAmber", icon: <WarningAmberIcon fontSize="small" /> },
  { label: "Cancel", value: "Cancel", icon: <CancelIcon fontSize="small" /> },
  { label: "HourglassEmpty", value: "HourglassEmpty", icon: <HourglassEmptyIcon fontSize="small" /> },
  { label: "DoneAll", value: "DoneAll", icon: <DoneAllIcon fontSize="small" /> },
  { label: "Loop", value: "Loop", icon: <LoopIcon fontSize="small" /> },
];

const getIconComponent = (value) => {
  const match = iconOptions.find((i) => i.value === value);
  return match ? match.icon : null;
};

const StatusesPage = () => {
  const theme = useTheme();
  const [statuses, setStatuses] = useState([]);
  const [modules, setModules] = useState([]);
  const [form, setForm] = useState({ module_id: "", statusname: "", alert_time: "", icon: "", color: "" });
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    fetchStatuses();
    fetchModules();
  }, []);

  const fetchStatuses = async () => {
    try {
      const res = await api.get("/statuses");
      setStatuses(res.data);
    } catch (error) {
      console.error("Error fetching statuses:", error);
    }
  };

  const fetchModules = async () => {
    try {
      const res = await api.get("/modules");
      setModules(res.data);
    } catch (error) {
      console.error("Error fetching modules:", error);
    }
  };

  const getModuleName = (id) => {
    const mod = modules.find((m) => m.id === id);
    return mod ? mod.name : id;
  };

  const getColorValue = (colorName) => {
    return theme.palette[colorName]?.main || customColors[colorName] || colorName;
  };

  const handleOpenForm = (status = null) => {
    if (status) {
      setForm({
        ...status,
        module_id: String(status.module_id),
      });
      setEditId(status.id);
    } else {
      setForm({ module_id: "", statusname: "", alert_time: "", icon: "", color: "" });
      setEditId(null);
    }
    setOpen(true);
  };

  const handleCloseForm = () => {
    setOpen(false);
    setEditId(null);
    setForm({ module_id: "", statusname: "", alert_time: "", icon: "", color: "" });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      if (editId) {
        await api.put(`/statuses/${editId}`, form);
        setSnack({ open: true, message: "Status updated", severity: "success" });
      } else {
        await api.post("/statuses", form);
        setSnack({ open: true, message: "Status added", severity: "success" });
      }
      handleCloseForm();
      fetchStatuses();
    } catch (error) {
      setSnack({ open: true, message: "Save failed", severity: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this status?")) return;
    try {
      await api.delete(`/statuses/${id}`);
      fetchStatuses();
      setSnack({ open: true, message: "Deleted successfully", severity: "success" });
    } catch (err) {
      setSnack({ open: true, message: "Delete failed", severity: "error" });
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">Statuses</Typography>
        <Button variant="contained" onClick={() => handleOpenForm()}>Add Status</Button>
      </Box>

      <Table sx={{ mt: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Status Name</TableCell>
            <TableCell>Module</TableCell>
            <TableCell>Alert Time</TableCell>
            <TableCell>Icon</TableCell>
            <TableCell>Color</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {statuses.map((status) => (
            <TableRow key={status.id}>
              <TableCell>{status.statusname}</TableCell>
              <TableCell>{getModuleName(status.module_id)}</TableCell>
              <TableCell>{status.alert_time}</TableCell>
              <TableCell>{getIconComponent(status.icon)}</TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      backgroundColor: getColorValue(status.color),
                      border: "1px solid #ccc"
                    }}
                  />
                  
                </Box>
              </TableCell>
              <TableCell align="right">
                <IconButton onClick={() => handleOpenForm(status)}><Edit /></IconButton>
                <IconButton onClick={() => handleDelete(status.id)}><Delete /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={handleCloseForm} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? "Edit Status" : "Add Status"}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ marginBottom: 2 }}>
          <InputLabel>Module</InputLabel>
        <Select
          name="module_id"
          value={form.module_id}
          onChange={handleChange}
           label="Module"
        >
    {modules.map((mod) => (
      <MenuItem key={mod.id} value={String(mod.id)}>
        {mod.name}
      </MenuItem>
    ))}
  </Select>
</FormControl>

          <TextField fullWidth label="Status Name" name="statusname" value={form.statusname} onChange={handleChange} margin="normal" />
          <TextField fullWidth label="Alert Time" name="alert_time" value={form.alert_time} onChange={handleChange} margin="normal" />

          {/* Icon Dropdown */}
          <FormControl fullWidth sx={{ marginBottom: 2, marginTop: 2 }}>
            <InputLabel>Icon</InputLabel>
            <Select name="icon" value={form.icon} onChange={handleChange} label="Icon">
              {iconOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {opt.icon}
                    {opt.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Color Dropdown */}
          <FormControl fullWidth sx={{ marginBottom: 2 }}>
  <InputLabel>Color</InputLabel>
  <Select name="color" value={form.color} onChange={handleChange} label="Color">
    {colorOptions.map((opt) => (
      <MenuItem key={opt.value} value={opt.value}>
        <Box
          sx={{
            height: 24,
            width: "100%",
            borderRadius: 1,
            backgroundColor: getColorValue(opt.value),
            border: "1px solid #ccc",
            mt: 0.5,
            mb: 0.5,
          }}
        />
      </MenuItem>
    ))}
  </Select>
</FormControl>


        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm} color="secondary">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default StatusesPage;
